import { useState, useEffect, useRef, useCallback } from 'react';
import { Layer } from '../../services/api';
import './LayerDescriptionPanel.css';

interface LayerDescriptionPanelProps {
  /** The layer being edited/viewed */
  layer: Layer | null;
  /** Whether in edit mode (true) or view mode (false) */
  isEditMode: boolean;
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback to close the panel */
  onClose: () => void;
  /** Callback when description changes (only in edit mode) */
  onDescriptionChange?: (layerId: string, description: string) => void;
  /** Whether the LayerSelector panel is open (affects positioning) */
  isLayerSelectorOpen?: boolean;
}

/**
 * Renders markdown content with support for HTML/CSS
 * Uses dangerouslySetInnerHTML for HTML support
 */
function renderMarkdown(markdown: string): string {
  if (!markdown) return '';

  let html = markdown;

  // Check if content starts with HTML tag - if so, treat as raw HTML
  const trimmed = html.trim();
  if (trimmed.startsWith('<') && !trimmed.startsWith('<http')) {
    return html;
  }

  // Headers
  html = html.replace(/^######\s+(.*)$/gm, '<h6>$1</h6>');
  html = html.replace(/^#####\s+(.*)$/gm, '<h5>$1</h5>');
  html = html.replace(/^####\s+(.*)$/gm, '<h4>$1</h4>');
  html = html.replace(/^###\s+(.*)$/gm, '<h3>$1</h3>');
  html = html.replace(/^##\s+(.*)$/gm, '<h2>$1</h2>');
  html = html.replace(/^#\s+(.*)$/gm, '<h1>$1</h1>');

  // Bold and italic
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/___(.+?)___/g, '<strong><em>$1</em></strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');

  // Strikethrough
  html = html.replace(/~~(.+?)~~/g, '<del>$1</del>');

  // Code blocks (must be before inline code)
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code class="language-$1">$2</code></pre>');

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Images
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');

  // Unordered lists
  html = html.replace(/^\s*[-*+]\s+(.*)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>');

  // Ordered lists
  html = html.replace(/^\s*\d+\.\s+(.*)$/gm, '<li>$1</li>');

  // Blockquotes
  html = html.replace(/^>\s+(.*)$/gm, '<blockquote>$1</blockquote>');
  html = html.replace(/<\/blockquote>\n<blockquote>/g, '\n');

  // Horizontal rules
  html = html.replace(/^---+$/gm, '<hr />');
  html = html.replace(/^\*\*\*+$/gm, '<hr />');

  // Paragraphs - wrap lines that aren't already wrapped
  const lines = html.split('\n');
  const wrappedLines = lines.map(line => {
    const trimmedLine = line.trim();
    if (!trimmedLine) return '';
    if (trimmedLine.startsWith('<')) return line;
    return `<p>${line}</p>`;
  });
  html = wrappedLines.join('\n');

  // Clean up empty paragraphs
  html = html.replace(/<p>\s*<\/p>/g, '');

  return html;
}

/**
 * Panel for viewing/editing layer descriptions in markdown format
 * In edit mode: Shows a textarea with markdown source and a preview toggle
 * In view mode: Shows rendered markdown content (read-only)
 */
function LayerDescriptionPanel({
  layer,
  isEditMode,
  isOpen,
  onClose,
  onDescriptionChange,
  isLayerSelectorOpen = true
}: LayerDescriptionPanelProps): JSX.Element | null {
  const [content, setContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Update content when layer changes
  useEffect(() => {
    if (layer) {
      setContent(layer.description || '');
    }
  }, [layer]);

  // Focus textarea when opening in edit mode
  useEffect(() => {
    if (isOpen && isEditMode && textareaRef.current && !showPreview) {
      textareaRef.current.focus();
    }
  }, [isOpen, isEditMode, showPreview]);

  /**
   * Handles content change and notifies parent
   */
  const handleContentChange = useCallback((newContent: string) => {
    setContent(newContent);
    if (layer && onDescriptionChange) {
      onDescriptionChange(layer.id, newContent);
    }
  }, [layer, onDescriptionChange]);

  /**
   * Toggle between edit and preview mode
   */
  const togglePreview = useCallback(() => {
    setShowPreview(prev => !prev);
  }, []);

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Escape to close
    if (e.key === 'Escape') {
      onClose();
    }
    // Ctrl+P to toggle preview
    if (e.ctrlKey && e.key === 'p') {
      e.preventDefault();
      togglePreview();
    }
  }, [onClose, togglePreview]);

  if (!isOpen || !layer) return null;

  const renderedContent = renderMarkdown(content);
  const panelClassName = `layer-description-panel ${!isLayerSelectorOpen ? 'standalone' : ''}`;

  return (
    <div className={panelClassName} onKeyDown={handleKeyDown}>
      <div className="layer-description-header">
        <div className="layer-description-title">
          <span className="layer-description-layer-name">{layer.name}</span>
          <span className="layer-description-subtitle">
            {isEditMode ? 'Edit Description' : 'Description'}
          </span>
        </div>
        <div className="layer-description-actions">
          {isEditMode && (
            <button
              className={`layer-description-btn ${showPreview ? 'active' : ''}`}
              onClick={togglePreview}
              title={showPreview ? 'Edit (Ctrl+P)' : 'Preview (Ctrl+P)'}
            >
              <i className={`fas ${showPreview ? 'fa-edit' : 'fa-eye'}`}></i>
            </button>
          )}
          <button
            className="layer-description-close"
            onClick={onClose}
            title="Close (Escape)"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div className="layer-description-content">
        {isEditMode && !showPreview ? (
          <textarea
            ref={textareaRef}
            className="layer-description-editor"
            value={content}
            onChange={(e) => handleContentChange(e.target.value)}
            placeholder="Write your description here using Markdown or HTML/CSS..."
            spellCheck={false}
          />
        ) : (
          <div
            className="layer-description-preview"
            dangerouslySetInnerHTML={{ __html: renderedContent }}
          />
        )}
      </div>

      {isEditMode && !showPreview && (
        <div className="layer-description-footer">
          <span className="layer-description-hint">
            Supports Markdown and HTML/CSS
          </span>
        </div>
      )}
    </div>
  );
}

export default LayerDescriptionPanel;
